import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Swal from "sweetalert2";
import { api, fetchData } from "../helper/api.interceptor";
import AddWareHouseDetails from "./AddWareHouseDetails";
import EditWarehouseDetails from "./EditWarehouseDetails";

function WareHouseDetails() {
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const itemsPerPage = 10;

    const fetchWarehouses = async () => {
        try {
            setLoading(true);
            const response = await fetchData.get("/get-all-warehouse");
            const data = response?.result || [];
            setWarehouses(data);
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWarehouses();
    }, []);

    const filteredWarehouses = warehouses.filter((warehouse) => {
        const query = searchQuery.trim().toLowerCase();
        return (
            warehouse.warehouse_name?.toLowerCase().includes(query) ||
            warehouse.warehouse_id?.toLowerCase().includes(query) ||
            warehouse.state?.toLowerCase().includes(query) ||
            warehouse.district?.toLowerCase().includes(query) ||
            warehouse.city?.toLowerCase().includes(query) ||
            warehouse.area?.toLowerCase().includes(query) ||
            warehouse.pincode?.toLowerCase().includes(query)
        );
    });

    const totalPages = Math.ceil(filteredWarehouses.length / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = currentPage * itemsPerPage;
    const currentWarehouses = filteredWarehouses.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [filteredWarehouses]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleDeleteClick = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/api/v1/warehouses/${id}`);
                setWarehouses(prev => prev.filter(w => w._id !== id));
                Swal.fire("Deleted!", "The warehouse has been deleted.", "success");
            } catch {
                Swal.fire("Error!", "Failed to delete the warehouse.", "error");
            }
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">  <Icon icon="mdi:warehouse" className="menu-icon" style={{ width: "30px", height: "30px" }} />  | Warehouse Details</h6>
                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link to="/" className="d-flex align-items-center gap-1 hover-text-primary">
                            <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                            Dashboard
                        </Link>
                    </li>
                    <li> - </li>
                    <li className="fw-medium">Warehouse Details</li>
                </ul>
            </div>

            <div className="card">
                <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="btn btn-warning rounded-pill fw-medium d-flex align-items-center gap-2"
                    >
                        <Icon icon="mdi:plus" className="fs-5" />
                        Add Warehouse
                    </button>
                </div>

                <div className="card-body" style={{ overflowX: "auto" }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Warehouse Name</th>
                                    <th>Warehouse ID</th>
                                    <th>State</th>
                                    <th>Action</th>
                                    {/* <th>Stock Details</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {currentWarehouses.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center">No warehouses found.</td>
                                    </tr>
                                ) : (
                                    currentWarehouses.map((warehouse, index) => (
                                        <tr key={warehouse._id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>{warehouse.warehouse_name || "N/A"}</td>
                                            <td>{warehouse.warehouse_id || "N/A"}</td>
                                            <td>{warehouse.state || "N/A"}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditId(warehouse._id);
                                                            setShowEditModal(true);
                                                        }}
                                                        className="w-32-px h-32-px bg-warning-subtle text-warning rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                    >
                                                        <Icon icon="mdi:pencil-outline" className="fs-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(warehouse._id)}
                                                        className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                    >
                                                        <Icon icon="mingcute:delete-2-line" />
                                                    </button>
                                                </div>
                                            </td>
                                            {/* <td>
                                                <td>
                                                    <Link
                                                        to={`/customer-order-details/${encodeURIComponent(
                                                            farmer?.phone_number
                                                        )}`}
                                                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    >
                                                        <Icon icon="iconamoon:eye-light" />
                                                    </Link>
                                                </td>
                                            </td> */}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add Warehouse Modal */}
            <AddWareHouseDetails
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onProviderAdded={fetchWarehouses}
            />
            {showEditModal && editId && (
                <EditWarehouseDetails
                    warehouseId={editId}       // renamed for clarity
                    show={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onWarehouseUpdated={fetchWarehouses}
                />
            )}
        </div>
    );
}
export default WareHouseDetails;